import { useNavigate } from '@tanstack/react-router';
import { Button, TextFieldPassword } from '@yembo/yemblocks-core';
import { useCallback, useEffect, useState } from 'react';
import { MfaEmployee, putVerifyMfa } from 'src/queries/Login';
import { useSubmitOnEnterKey } from 'src/react/_hooks/useEnterKeySubmit';
import { LoginFormData } from '..';
import { loginDevice, notifyLockedAccount } from '../_util/common';
import { SectionHeader } from '../SectionHeader';

type Props = {
  formData: LoginFormData;
};

export const VerifyBackupCode = (props: Props) => {
  const { mfaEmployee } = props.formData;

  useEffect(() => {
    if (!mfaEmployee) {
      window.location.assign('/');
    }
  }, [mfaEmployee]);

  if (!mfaEmployee) return null;

  return <VerifyBackupCodeInternal {...props} mfaEmployee={mfaEmployee} />;
};

const VerifyBackupCodeInternal = ({ formData }: Props & { mfaEmployee: MfaEmployee }) => {
  const [backupCode, setBackupCode] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const onLogin = useCallback(async () => {
    if (!(backupCode.length === 16 && /^[a-zA-Z0-9]+$/.test(backupCode))) {
      setIsError(true);
      return;
    }

    const mfaData = {
      email: formData.email ?? '',
      password: formData.password ?? '',
      backupCode,
    };

    setIsLoading(true);

    try {
      const response = await putVerifyMfa(mfaData);
      loginDevice(response);
    } catch (error) {
      notifyLockedAccount(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [backupCode, formData.email, formData.password]);

  useSubmitOnEnterKey({ onSubmit: onLogin });

  return (
    <div className='verify-backup-code authentication-form'>
      <SectionHeader heading='Verify Account' caption='Enter your backup code below to log in.' />
      <TextFieldPassword
        name='backupCode'
        label='Backup code'
        onChange={(event) => {
          setBackupCode(event.target.value);
          if (isError) setIsError(false);
        }}
        isValid={!isError}
        errorMessage='Incorrect code'
      />
      <div className='buttons-wrapper'>
        <Button text='Login' isLoading={isLoading} onClick={onLogin} />
        <Button
          text='Back'
          kind='outline'
          // biome-ignore lint/suspicious/noExplicitAny: router constraint bypass
          onClick={() => navigate({ to: '/verify-mfa', search: true } as any)}
          isDisabled={isLoading}
        />
      </div>
    </div>
  );
};
